/**
 * Role Permissions Utility
 * Implements role inheritance: Employee < Manager < Finance/HR Admin < Super Admin
 * Each higher role has all capabilities of lower roles plus their own
 */

import { UserRole } from '../types/user';
import { sameIdentity } from '../services/identity';

/**
 * Role hierarchy levels (higher number = more permissions)
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.EMPLOYEE]: 1,
  [UserRole.MANAGER]: 2,
  [UserRole.FINANCE_ADMIN]: 3,
  [UserRole.HR_ADMIN]: 3,
  [UserRole.SUPER_ADMIN]: 4
};

/**
 * Permission types available in the system
 */
export enum Permission {
  // Employee permissions
  CREATE_EXPENSE = 'CREATE_EXPENSE',
  VIEW_OWN_EXPENSES = 'VIEW_OWN_EXPENSES',
  EDIT_OWN_DRAFT = 'EDIT_OWN_DRAFT',
  DELETE_OWN_DRAFT = 'DELETE_OWN_DRAFT',
  SUBMIT_EXPENSE = 'SUBMIT_EXPENSE',
  VIEW_OWN_PROFILE = 'VIEW_OWN_PROFILE',
  
  // Manager permissions (includes all Employee permissions)
  VIEW_TEAM_EXPENSES = 'VIEW_TEAM_EXPENSES',
  APPROVE_TEAM_EXPENSES = 'APPROVE_TEAM_EXPENSES',
  REJECT_TEAM_EXPENSES = 'REJECT_TEAM_EXPENSES',
  VIEW_TEAM_MEMBERS = 'VIEW_TEAM_MEMBERS',
  
  // Finance Admin permissions (includes all Manager permissions)
  VIEW_ALL_EXPENSES = 'VIEW_ALL_EXPENSES',
  APPROVE_FINANCE = 'APPROVE_FINANCE',
  REJECT_FINANCE = 'REJECT_FINANCE',
  MARK_REIMBURSED = 'MARK_REIMBURSED',
  VIEW_REPORTS = 'VIEW_REPORTS',
  EXPORT_DATA = 'EXPORT_DATA',
  
  // Super Admin permissions (includes all Finance Admin permissions)
  MANAGE_USERS = 'MANAGE_USERS',
  CREATE_USER = 'CREATE_USER',
  EDIT_USER = 'EDIT_USER',
  DELETE_USER = 'DELETE_USER',
  CHANGE_USER_ROLE = 'CHANGE_USER_ROLE',
  VIEW_AUDIT_LOGS = 'VIEW_AUDIT_LOGS',
  SYSTEM_SETTINGS = 'SYSTEM_SETTINGS'
}

/**
 * Permissions granted to each role (cumulative with lower roles)
 */
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.EMPLOYEE]: [
    Permission.CREATE_EXPENSE,
    Permission.VIEW_OWN_EXPENSES,
    Permission.EDIT_OWN_DRAFT,
    Permission.DELETE_OWN_DRAFT,
    Permission.SUBMIT_EXPENSE,
    Permission.VIEW_OWN_PROFILE
  ],
  [UserRole.MANAGER]: [
    Permission.VIEW_TEAM_EXPENSES,
    Permission.APPROVE_TEAM_EXPENSES,
    Permission.REJECT_TEAM_EXPENSES,
    Permission.VIEW_TEAM_MEMBERS
  ],
  [UserRole.FINANCE_ADMIN]: [
    Permission.VIEW_ALL_EXPENSES,
    Permission.APPROVE_FINANCE,
    Permission.REJECT_FINANCE,
    Permission.MARK_REIMBURSED,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA
  ],
  [UserRole.HR_ADMIN]: [],
  [UserRole.SUPER_ADMIN]: [
    Permission.MANAGE_USERS,
    Permission.CREATE_USER,
    Permission.EDIT_USER,
    Permission.DELETE_USER,
    Permission.CHANGE_USER_ROLE,
    Permission.VIEW_AUDIT_LOGS,
    Permission.SYSTEM_SETTINGS
  ]
};

/**
 * Get the hierarchy level of a role
 */
export const getRoleLevel = (role: UserRole): number => {
  return ROLE_HIERARCHY[role] || 0;
};

/**
 * Check if role1 is higher or equal to role2 in hierarchy
 */
export const isRoleHigherOrEqual = (role1: UserRole, role2: UserRole): boolean => {
  return getRoleLevel(role1) >= getRoleLevel(role2);
};

/**
 * Check if role1 is strictly higher than role2 in hierarchy
 */
export const isRoleHigher = (role1: UserRole, role2: UserRole): boolean => {
  return getRoleLevel(role1) > getRoleLevel(role2);
};

/**
 * Get all permissions for a role (including inherited permissions from lower roles)
 */
export const getPermissionsForRole = (role: UserRole): Permission[] => {
  const permissions: Permission[] = [];
  const roleLevel = getRoleLevel(role);
  
  // Add permissions from this role and all lower roles
  Object.entries(ROLE_HIERARCHY).forEach(([r, level]) => {
    if (level <= roleLevel) {
      permissions.push(...ROLE_PERMISSIONS[r as UserRole]);
    }
  });
  
  return [...new Set(permissions)]; // Remove duplicates
};

/**
 * Check if a role has a specific permission
 */
export const hasPermission = (role: UserRole, permission: Permission): boolean => {
  const permissions = getPermissionsForRole(role);
  return permissions.includes(permission);
};

/**
 * Check if user can approve expenses (Manager level or higher)
 */
export const canApproveExpenses = (role: UserRole): boolean => {
  return isRoleHigherOrEqual(role, UserRole.MANAGER);
};

/**
 * Check if user can approve finance (Finance Admin or Super Admin)
 */
export const canApproveFinance = (role: UserRole): boolean => {
  return role === UserRole.FINANCE_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Who may give the ADMIN decision on a certification reimbursement.
 *
 * ── Two sources of the right, deliberately OR-ed ──────────────────────────────────────────
 *
 * The existing finance admins keep it — they are not replaced, and a configuration change must
 * never be able to lock them out of a queue they are accountable for. On top of them, Settings →
 * Certifications names one additional employee who may approve certifications AND NOTHING ELSE.
 *
 * ── Why the extra approver is not a role ──────────────────────────────────────────────────
 *
 * Promoting that person to Finance_Admin would hand them every other expense queue, the reports,
 * the exports and Settings. The requirement is explicit that it must not: they get certification
 * approval only. So the capability is carried by an EMAIL held in configuration, checked
 * alongside the role, and their UserRole never changes.
 *
 * `certificationAdminEmail` is the configured address, or undefined when nobody is configured —
 * in which case this is exactly the existing admin check and nothing has widened.
 *
 * ── Compared with sameIdentity, never with toLowerCase ────────────────────────────────────
 *
 * This organisation addresses one person on two interchangeable domains — creyentetech.com and
 * creyenteinfotech.com — and a native and a federated account for the same employee routinely
 * carry different ones. A raw lowercase comparison therefore fails for exactly the person it was
 * meant to match: the address saved in Settings and the address on their session are the same
 * human and two different strings, so the grant silently did nothing.
 *
 * sameIdentity canonicalises both sides before comparing, and returns false when either is
 * blank — which also closes the hole where an employee with no email on their session would
 * match an unset setting and inherit approval from two absences.
 */
/*
 * ── Certification reimbursement: three INDEPENDENT gates ──────────────────────────────────
 *
 * One per stage of the flow, evaluated separately and combined additively. A person can be a
 * manager AND the configured certification admin AND finance at once; asking "which one are
 * they?" would hide pages from anybody holding two responsibilities, which is why these are
 * three predicates rather than one switch.
 *
 * Deliberately NOT built on canViewTeamExpenses. That helper is `role >= MANAGER`, and the role
 * ladder puts Finance Admin above Manager — so reusing it would hand the manager queue to
 * Finance, who has no reporting line and would see an empty page they were never meant to work.
 * Each gate names the roles it means.
 *
 * None of these knows any person. Identity comes from the session; the manager relationship from
 * the claim's own managerEmail; the certification admin from Settings.
 */

/**
 * Stage 1 — the manager queue.
 *
 * A MANAGER qualifies by role; whose claims they actually see is decided by the claim's
 * managerEmail, not here. HR and Super Admin qualify for oversight.
 */
export const canViewCertificationApprovals = (role: UserRole): boolean =>
  role === UserRole.MANAGER || role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;

/**
 * Stage 2 — the certification admin queue.
 *
 * The configured employee from Settings > Certifications, compared through sameIdentity so the
 * two interchangeable domains resolve to one person. Changing the setting moves the access with
 * it: nothing here remembers who held it before.
 */
export const canViewCertificateAdminApproval = (
  role: UserRole,
  userEmail?: string,
  certificationAdminEmail?: string
): boolean =>
  sameIdentity(userEmail, certificationAdminEmail) ||
  role === UserRole.HR_ADMIN ||
  role === UserRole.SUPER_ADMIN;

/** Stage 3 — the finance queue. Certification claims only; EMS expenses are a separate flow. */
export const canViewFinanceReimbursements = (role: UserRole): boolean =>
  role === UserRole.FINANCE_ADMIN ||
  role === UserRole.HR_ADMIN ||
  role === UserRole.SUPER_ADMIN;

export const canApproveCertifications = (
  role: UserRole,
  userEmail?: string,
  certificationAdminEmail?: string
): boolean => {
  if (canApproveFinance(role)) return true;
  return sameIdentity(userEmail, certificationAdminEmail);
};

/**
 * Whether this person is the configured Certification Admin and NOT otherwise an admin.
 *
 * Used to keep the grant narrow on screen: someone who holds certification approval only must
 * not be shown finance queues, reports or Settings. Answering "are they an admin?" with a plain
 * yes is what would leak those.
 */
export const isCertificationAdminOnly = (
  role: UserRole,
  userEmail?: string,
  certificationAdminEmail?: string
): boolean =>
  !canApproveFinance(role) && canApproveCertifications(role, userEmail, certificationAdminEmail);

/**
 * Check if user can manage users (Super Admin or HR Admin)
 */
export const canManageUsers = (role: UserRole): boolean => {
  return role === UserRole.SUPER_ADMIN || role === UserRole.HR_ADMIN;
};

/**
 * Check if user can view all expenses (Finance Admin, HR Admin, or Super Admin)
 */
export const canViewAllExpenses = (role: UserRole): boolean => {
  return role === UserRole.FINANCE_ADMIN || role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Check if user can view team expenses (Manager level or higher)
 */
export const canViewTeamExpenses = (role: UserRole): boolean => {
  return isRoleHigherOrEqual(role, UserRole.MANAGER);
};

/**
 * Check if user can create expenses (all roles can)
 */
export const canCreateExpense = (role: UserRole): boolean => {
  return hasPermission(role, Permission.CREATE_EXPENSE);
};

/**
 * Get the dashboard route for a role
 * Higher roles can access lower role dashboards
 */
export const getDashboardsForRole = (role: UserRole): string[] => {
  const dashboards: string[] = [];
  
  // All roles have employee dashboard
  dashboards.push('/dashboard');
  
  if (isRoleHigherOrEqual(role, UserRole.MANAGER)) {
    dashboards.push('/manager');
  }
  
  if (isRoleHigherOrEqual(role, UserRole.FINANCE_ADMIN)) {
    dashboards.push('/finance');
  }
  
  if (isRoleHigherOrEqual(role, UserRole.SUPER_ADMIN)) {
    dashboards.push('/admin');
  }
  
  return dashboards;
};

/**
 * Get the primary dashboard for a role
 */
export const getPrimaryDashboard = (role: UserRole): string => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return '/admin';
    case UserRole.FINANCE_ADMIN:
      return '/finance';
    case UserRole.HR_ADMIN:
      return '/dashboard';
    case UserRole.MANAGER:
      return '/manager';
    default:
      return '/dashboard';
  }
};

/**
 * Check if user can access a specific route based on role
 */
export const canAccessRoute = (role: UserRole, route: string): boolean => {
  const accessibleDashboards = getDashboardsForRole(role);
  
  // Check if route starts with any accessible dashboard
  return accessibleDashboards.some(dashboard => 
    route === dashboard || route.startsWith(dashboard + '/')
  );
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    case UserRole.FINANCE_ADMIN:
      return 'Finance Admin';
    case UserRole.HR_ADMIN:
      return 'HR Admin';
    case UserRole.MANAGER:
      return 'Manager';
    case UserRole.EMPLOYEE:
      return 'Employee';
    default:
      return role;
  }
};

/**
 * Get all roles that a user with given role can assign to others
 * Users can only assign roles lower than their own
 */
export const getAssignableRoles = (userRole: UserRole): UserRole[] => {
  const userLevel = getRoleLevel(userRole);
  
  return Object.entries(ROLE_HIERARCHY)
    .filter(([_, level]) => level < userLevel)
    .map(([role, _]) => role as UserRole);
};

// ============================================
// Shift Roster Module Permissions
// ============================================

/**
 * Check if user can access the Shift Roster module.
 * Available to: Manager, HR_Admin, Super_Admin
 * NOT available to: Employee, Finance_Admin
 */
export const canAccessShiftRoster = (role: UserRole): boolean => {
  return role === UserRole.MANAGER || role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Check if user can *view* Shift Details.
 *
 * Everyone, deliberately. canAccessShiftRoster below governs editing and still excludes
 * Employee — the two were previously the same check, so an employee could not reach the screen
 * at all and had no way to see their own roster. The backend already draws this same line:
 * requireRosterAccess in the shift-roster Lambda guards only the four assignment mutations,
 * while the read paths are plain Cognito-authorised queries. So opening the view adds no
 * capability that the API did not already permit.
 */
export const canViewShiftRoster = (_role: UserRole): boolean => true;

/**
 * Whether `role` may edit *this particular* employee's roster.
 *
 * Split from canAccessShiftRoster because visibility and editability stopped being the same
 * question: every role now sees every employee in Shift Details, but a Manager may only change
 * the roster of their own team — themselves and the people who report to them. HR_Admin and
 * Super_Admin are unrestricted; Employee (and Finance_Admin) can edit nobody, their own row
 * included, because a shift is set for them rather than by them.
 *
 * The self case is a separate clause rather than something the reporting-line check happens to
 * cover: a manager's own `managerEmail` names *their* manager, never themselves, so matching on
 * the reporting line alone left every manager unable to record their own shift — the one row on
 * the grid they are certain to know about.
 *
 * `sameIdentityFn` is injected so this module stays free of the identity service — callers pass
 * `sameIdentity`, which matches a person across their two domain accounts. That matters for both
 * clauses here: the viewer may be signed in on either domain, so the self check is no more an
 * exact string comparison than the reporting-line one is. An employee whose manager is unset is
 * editable only by an admin (or by themselves, if they are a Manager), which is the safe reading
 * of missing data.
 *
 * The shift-roster Lambda re-checks both clauses on every mutation, so this governs the UI's
 * affordances rather than being the only line of defence.
 */
export const canEditEmployeeRoster = (
  role: UserRole,
  employeeEmail: string | undefined,
  managerEmail: string | undefined,
  viewerEmail: string | undefined,
  sameIdentityFn: (a?: string | null, b?: string | null) => boolean
): boolean => {
  if (!canAccessShiftRoster(role)) return false;
  if (role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN) return true;
  // A Manager's own row, then their reports'.
  if (sameIdentityFn(employeeEmail, viewerEmail)) return true;
  return sameIdentityFn(managerEmail, viewerEmail);
};

/**
 * Check if user can manage Shift Master (create/edit/delete shifts).
 * Available to: HR_Admin, Super_Admin
 * NOT available to: Manager, Employee, Finance_Admin
 */
export const canManageShiftMaster = (role: UserRole): boolean => {
  return role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Check if user can open Shift Roster → Reports.
 * Available to: HR_Admin, Super_Admin
 * NOT available to: Manager, Employee, Finance_Admin
 *
 * Deliberately its own function rather than reusing canManageShiftMaster, which happens to cover
 * the same two roles today: that one answers "may this person create and edit shift definitions",
 * and a reporting page is not a management capability. Tying them together would mean a later
 * change to who may configure shifts silently moved who may export the roster.
 *
 * Reports is stricter than the roster it reads. Shift Details is visible to everyone and Bulk
 * Override is open to Managers, but a report is a bulk extract of the whole directory — every
 * employee's department, manager, location, employment type and status in one downloadable file —
 * so it follows the org-wide access line (canViewAllShiftRoster) rather than the roster-editing
 * one. The page reads org-wide precisely because only roles that already see every employee can
 * reach it.
 */
export const canAccessShiftReports = (role: UserRole): boolean => {
  return role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Whether `role` may decide an attendance regularization request.
 *
 * Manager, HR_Admin, Super_Admin. NOT Finance_Admin, and not Employee.
 *
 * ── Why this is not canViewTeamExpenses ───────────────────────────────────────────────────
 *
 * Both approver gates used to read canViewTeamExpenses, which is a question about EXPENSES and
 * happens to include Finance_Admin. The server never agreed: resolveCorrectionRequest in the
 * attendance-sync Lambda accepts an admin (Super_Admin/HR_Admin) or the employee's assigned
 * manager, and Finance_Admin is neither. So a Finance Admin was shown an Approvals tab, a
 * worklist and Approve buttons, and every click came back Unauthorized — the UI promising a
 * capability the API had never granted.
 *
 * Attendance is not a finance matter. Deciding whether somebody was at their desk is a
 * reporting-line judgement, which is why the server drew the line where it did; this brings the
 * UI to the same line rather than widening the server to match a mistake.
 *
 * A Finance Admin who genuinely manages people is not stranded by this: requests route to the
 * assigned manager AND to every HR admin by email, so those still get decided.
 */
export const canApproveRegularization = (role: UserRole): boolean =>
  role === UserRole.MANAGER || role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;

/**
 * Check if user can manage Attendance Cycles.
 * Available to: HR_Admin, Super_Admin
 * NOT available to: Manager, Employee, Finance_Admin
 */
export const canManageAttendanceCycles = (role: UserRole): boolean => {
  return role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};

/**
 * Check if user can view all employees in Shift Roster (org-wide access).
 * Available to: HR_Admin, Super_Admin
 * Manager can only view direct reports (handled at service level).
 */
export const canViewAllShiftRoster = (role: UserRole): boolean => {
  return role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
};
